import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Home, 
  Ruler, 
  Eye,
  Download,
  Maximize
} from 'lucide-react';

const FloorPlanSection = () => {
  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const openVirtualTour = () => {
    window.open('https://vr.shinewonder.com/pano/page/publik/pklimit?inf=@DBFFDBMMFBBEFDA@', '_blank');
  };

  const floorPlanFeatures = [
    {
      room: "Salon/Séjour/Kitchenette",
      surface: "54.84 m²",
      description: "Espace de vie principal ouvert et lumineux"
    },
    {
      room: "Terrasse",
      surface: "37.78 m²", 
      description: "Grande terrasse avec vue panoramique"
    },
    {
      room: "Chambre Principale",
      surface: "24.36 m²",
      description: "Suite parentale avec salle de bain privative"
    },
    {
      room: "Cuisine",
      surface: "12.91 m²",
      description: "Cuisine moderne équipée"
    },
    {
      room: "3 Autres Chambres",
      surface: "14.68 m² à 16.07 m²",
      description: "Chambres spacieuses avec rangements"
    },
    {
      room: "Espace Famille/Lecture",
      surface: "24.67 m²",
      description: "Espace polyvalent pour la détente"
    }
  ];

  return (
    <section id="floor-plan" className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-6 bg-blue-100 text-blue-800 border-blue-200">
            <Home className="h-4 w-4 mr-2" />
            Plans d'Appartement
          </Badge>
          
          <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6">
            Découvrez la répartition
            <br />
            <span className="text-amber-600">de votre futur chez-vous</span>
          </h2>
          
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Un appartement F5 conçu pour optimiser chaque mètre carré, 
            avec des espaces de vie généreux et une distribution intelligente.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Floor Plan Image */}
          <div className="order-2 lg:order-1">
            <Card className="overflow-hidden shadow-2xl">
              <CardHeader className="bg-slate-800 text-white">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center">
                    <Ruler className="h-5 w-5 mr-2" />
                    Plan Appartement F5
                  </span>
                  <Badge className="bg-amber-600 text-white">
                    250 m²
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="relative group">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_perfect-match-17/artifacts/sn1h0rhm_APPART%20F5.jpg"
                    alt="Plan d'appartement F5 - Résidence NATOMA"
                    className="w-full h-auto transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Maximize className="h-12 w-12 text-white bg-black/50 rounded-full p-3" />
                    </div>
                  </div>
                </div>
                <div className="p-6 bg-slate-800 text-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold">Plan architectural détaillé</h4>
                      <p className="text-slate-300 text-sm">Surfaces exactes et aménagements</p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-amber-600 text-amber-600 hover:bg-amber-600 hover:text-white"
                      onClick={() => window.open('https://customer-assets.emergentagent.com/job_perfect-match-17/artifacts/sn1h0rhm_APPART%20F5.jpg', '_blank')}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Télécharger
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Features List */}
          <div className="order-1 lg:order-2">
            <h3 className="text-2xl font-bold text-slate-800 mb-8">
              Répartition des espaces
            </h3>
            
            <div className="space-y-4 mb-8">
              {floorPlanFeatures.map((feature, index) => (
                <Card key={index} className="hover:shadow-lg transition-all duration-300 border-l-4 border-l-amber-600">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-800 mb-1">{feature.room}</h4>
                        <p className="text-slate-600 text-sm">{feature.description}</p>
                      </div>
                      <Badge className="bg-amber-100 text-amber-800 ml-4">
                        {feature.surface}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Key Features */}
            <Card className="bg-gradient-to-br from-slate-800 to-slate-700 text-white">
              <CardContent className="p-6">
                <h4 className="text-xl font-bold mb-4 text-amber-400">
                  Points forts de l'aménagement
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-amber-400 rounded-full mr-3"></div>
                    <span>4 "Vides sur patio" pour une luminosité optimale</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-amber-400 rounded-full mr-3"></div>
                    <span>Grande terrasse de 37.78 m² avec vue panoramique</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-amber-400 rounded-full mr-3"></div>
                    <span>Espace famille/lecture de 24.67 m² polyvalent</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-amber-400 rounded-full mr-3"></div>
                    <span>Distribution optimisée pour l'intimité familiale</span>
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 bg-amber-400 rounded-full mr-3"></div>
                    <span>Multiples salles de bains et espaces de rangement</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Button 
                onClick={openVirtualTour}
                className="bg-amber-600 hover:bg-amber-700 text-white flex-1"
              >
                <Eye className="h-4 w-4 mr-2" />
                Visite virtuelle 360°
              </Button>
              <Button 
                onClick={scrollToContact}
                variant="outline"
                className="border-slate-800 text-slate-800 hover:bg-slate-800 hover:text-white flex-1"
              >
                Demander des infos
              </Button>
            </div>
          </div>
        </div>

        {/* Bottom Info */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
            <h3 className="text-2xl font-bold text-slate-800 mb-4">
              Vous souhaitez personnaliser l'aménagement ?
            </h3>
            <p className="text-slate-600 mb-6 max-w-2xl mx-auto">
              En tant qu'acquéreur VEFA, vous avez la possibilité de personnaliser 
              certains aménagements selon vos goûts et besoins familiaux.
            </p>
            <Badge className="bg-emerald-100 text-emerald-800 text-lg px-4 py-2">
              ✅ Personnalisation possible pendant la construction
            </Badge>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FloorPlanSection;